/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useLayoutEffect, useCallback} from 'react';
import {useMediaQuery} from 'react-responsive';
import {
  Button,
  Fab,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  Drawer,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Visibility,
  VisibilityOff,
  CheckCircleOutline,
  ArrowForward,
  ArrowBack,
  MyLocation,
} from '@mui/icons-material';
import ImageIcon from '@mui/icons-material/Image';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  updateProfile,
  signOut,
} from 'firebase/auth';
import {useDropzone} from 'react-dropzone';
import {GoogleMap, useJsApiLoader, Marker} from '@react-google-maps/api';
import InputMask from 'react-input-mask';
import styled, {keyframes} from 'styled-components';
import {Link} from 'react-router-dom';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://kops.apiireit.com/gateway';
const GOOGLE_MAPS_KEY = 'AIzaSyDDktPhGLUCBoDQfNJ9TbtSiVcCKFGTdsM';

// ─── Animations ───────────────────────────────────────────────────────────────
const slideUp = keyframes`
  from { transform: translateY(32px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position:  200% 0; }
`;
const SlideUp = styled.div`
  animation: ${slideUp} 0.55s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
};

// ─── Step config ──────────────────────────────────────────────────────────────
const STEPS = [
  {id: 'auth', label: 'Conta', icon: '🔐'},
  {id: 'info', label: 'Ponto', icon: '📋'},
  {id: 'logo', label: 'Logo', icon: '🖼️'},
  {id: 'address', label: 'Endereço', icon: '📍'},
  {id: 'confirm', label: 'Confirmar', icon: '✅'},
];

// ─── Step indicator ───────────────────────────────────────────────────────────
const StepBar = ({current, sm}) => {
  const idx = STEPS.findIndex(s => s.id === current);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sm ? 4 : 8,
        marginBottom: 32,
        flexWrap: 'wrap',
      }}
    >
      {STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={s.id}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <div
                style={{
                  width: sm ? 34 : 42,
                  height: sm ? 34 : 42,
                  borderRadius: '50%',
                  background: done
                    ? C.success
                    : active
                    ? C.laranja
                    : 'rgba(255,255,255,0.12)',
                  border: `2px solid ${
                    done
                      ? C.success
                      : active
                      ? C.laranja
                      : 'rgba(255,255,255,0.2)'
                  }`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: sm ? 14 : 18,
                  transition: 'all 0.3s ease',
                }}
              >
                {done ? '✓' : s.icon}
              </div>
              {!sm && (
                <span
                  style={{
                    fontSize: 11,
                    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                    fontWeight: active ? 700 : 400,
                  }}
                >
                  {s.label}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: sm ? 16 : 32,
                  height: 2,
                  marginBottom: sm ? 0 : 18,
                  background: i < idx ? C.success : 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  transition: 'background 0.3s ease',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── Card shell ───────────────────────────────────────────────────────────────
const Card = ({children, sm}) => (
  <div
    style={{
      background: C.white,
      borderRadius: sm ? 16 : 24,
      padding: sm ? '24px 16px' : '36px 32px',
      maxWidth: 520,
      width: '100%',
      margin: '0 auto',
      boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
      boxSizing: 'border-box',
    }}
  >
    {children}
  </div>
);

// ─── Section title ────────────────────────────────────────────────────────────
const STitle = ({children, sub}) => (
  <div style={{marginBottom: 24}}>
    <p style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}>
      {children}
    </p>
    {sub && (
      <p style={{margin: '4px 0 0', fontSize: 14, color: C.muted}}>{sub}</p>
    )}
  </div>
);

// ─── Navigation buttons ───────────────────────────────────────────────────────
const NavRow = ({
  onBack,
  onNext,
  nextLabel = 'Continuar',
  nextDisabled,
  loading,
}) => (
  <div
    style={{
      display: 'flex',
      gap: 12,
      marginTop: 28,
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    {onBack ? (
      <Button
        onClick={onBack}
        startIcon={<ArrowBack />}
        style={{textTransform: 'none', color: C.muted, fontSize: 14}}
      >
        Voltar
      </Button>
    ) : (
      <span />
    )}
    <Button
      onClick={onNext}
      disabled={nextDisabled || loading}
      endIcon={loading ? null : <ArrowForward />}
      sx={{
        bgcolor: C.laranja,
        color: '#fff',
        borderRadius: '12px',
        height: 48,
        px: 3,
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 15,
        ':hover': {bgcolor: C.laranjaD},
        ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
      }}
    >
      {loading ? (
        <CircularProgress size={20} style={{color: '#fff'}} />
      ) : (
        nextLabel
      )}
    </Button>
  </div>
);

// ─── Benefit pills ────────────────────────────────────────────────────────────
const benefits = [
  {icon: '💰', text: 'Sem mensalidade'},
  {icon: '📊', text: 'Painel completo'},
  {icon: '📱', text: 'App próprio'},
  {icon: '💬', text: 'WhatsApp automático'},
  {icon: '🗺️', text: 'Rastreamento em tempo real'},
  {icon: '🎯', text: 'Cupons e missões'},
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
const PontoCadastro = () => {
  const auth = getAuth();
  const sm = useMediaQuery({query: '(max-width: 750.98px)'});
  const vsm = useMediaQuery({query: '(max-width: 450px)'});

  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState('auth');
  const [authUser, setAuthUser] = useState(null);
  const [pontoExistente, setPontoExistente] = useState(null); // ponto já cadastrado pelo uid

  // ── auth state ────────────────────────────────────────────────────────────
  const [authMode, setAuthMode] = useState('register');
  const [authName, setAuthName] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPass, setAuthPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // ── ponto info ────────────────────────────────────────────────────────────
  const [nome, setNome] = useState('');
  const [nomeFantasia, setNomeFantasia] = useState('');
  const [celular, setCelular] = useState('');
  const [email, setEmail] = useState('');
  const [temCnpj, setTemCnpj] = useState(false);
  const [cnpj, setCnpj] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [infoError, setInfoError] = useState('');

  // ── logo ──────────────────────────────────────────────────────────────────
  const [logoFiles, setLogoFiles] = useState([]);

  // ── address ───────────────────────────────────────────────────────────────
  const [cep, setCep] = useState('');
  const [cepError, setCepError] = useState(false);
  const [estado, setEstado] = useState('');
  const [cidade, setCidade] = useState('');
  const [bairro, setBairro] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [comp, setComp] = useState('');
  const [marker, setMarker] = useState({lat: -12.9714, lng: -38.5014});
  const [mapReady, setMapReady] = useState(false);
  const [addrError, setAddrError] = useState('');

  // ── submit ────────────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Google Maps ───────────────────────────────────────────────────────────
  const {isLoaded: mapsLoaded} = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
  });

  // ── scroll + fab ──────────────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
    signOut(auth).catch(() => {});
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  // ── CEP lookup ────────────────────────────────────────────────────────────
  const fetchCep = useCallback(rawCep => {
    const clean = rawCep.replace(/\D/g, '');
    if (clean.length !== 8) return;
    fetch(`https://viacep.com.br/ws/${clean}/json/`)
      .then(r => r.json())
      .then(d => {
        if (d.erro) {
          setCepError(true);
          return;
        }
        setCepError(false);
        setEstado(d.uf || '');
        setCidade(d.localidade || '');
        setLogradouro(d.logradouro || '');
        setBairro(d.bairro || '');
      })
      .catch(() => setCepError(true));
  }, []);

  // ── Geocode address → lat/lng ─────────────────────────────────────────────
  const geocode = useCallback(() => {
    if (!logradouro || !cidade || !estado) return;
    const addr = encodeURIComponent(
      `${logradouro}, ${numero}, ${cidade}, ${estado}`,
    );
    fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${addr}&key=${GOOGLE_MAPS_KEY}`,
    )
      .then(r => r.json())
      .then(d => {
        if (d.results?.[0]) {
          const {lat, lng} = d.results[0].geometry.location;
          setMarker({lat, lng});
          setMapReady(true);
        }
      })
      .catch(() => {});
  }, [logradouro, numero, cidade, estado]);

  useEffect(() => {
    if (logradouro && cidade && estado) geocode();
  }, [logradouro, cidade, estado, numero]);

  // ─────────────────────────────────────────────────────────────────────────
  // AUTH HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const handleEmailAuth = async () => {
    setAuthError('');
    if (!authEmail.trim()) return setAuthError('Email obrigatório.');
    if (!authPass) return setAuthError('Senha obrigatória.');
    if (authMode === 'register' && !authName.trim())
      return setAuthError('Informe seu nome.');
    if (authMode === 'register' && authPass.length < 6)
      return setAuthError('Senha: mínimo 6 caracteres.');
    setAuthLoading(true);
    try {
      let cred;
      if (authMode === 'register') {
        cred = await createUserWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
        await updateProfile(cred.user, {displayName: authName.trim()});
      } else {
        cred = await signInWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
      }
      setAuthUser(cred.user);
      setEmail(cred.user.email || '');
      const hasExisting = await checkExistingPonto(cred.user);
      if (!hasExisting) setStep('info');
    } catch (err) {
      const map = {
        'auth/email-already-in-use': 'Email já cadastrado. Faça login.',
        'auth/wrong-password': 'Senha incorreta.',
        'auth/user-not-found': 'Usuário não encontrado.',
        'auth/invalid-credential': 'Email ou senha incorretos.',
        'auth/too-many-requests': 'Muitas tentativas. Tente mais tarde.',
      };
      setAuthError(map[err.code] || 'Erro ao autenticar.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogle = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await signInWithPopup(auth, new GoogleAuthProvider());
      setAuthUser(res.user);
      setEmail(res.user.email || '');
      setAuthName(res.user.displayName || '');
      const hasExistingG = await checkExistingPonto(res.user);
      if (!hasExistingG) setStep('info');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Google.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleApple = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const res = await signInWithPopup(auth, provider);
      setAuthUser(res.user);
      setEmail(res.user.email || '');
      const hasExistingA = await checkExistingPonto(res.user);
      if (!hasExistingA) setStep('info');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Apple.');
    } finally {
      setAuthLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // CHECK EXISTING PONTO
  // Consulta GET /parceiros/pontos/get/gestor?gestorFirebaseid={uid}
  // Retorna true se o usuário já tem ponto (interrompe o fluxo do form)
  // ─────────────────────────────────────────────────────────────────────────
  const checkExistingPonto = async firebaseUser => {
    try {
      const token = await firebaseUser.getIdToken();
      const res = await fetch(
        `${API_BASE}/parceiros/pontos/get/gestor?gestorFirebaseid=${firebaseUser.uid}`,
        {headers: {Authorization: `Bearer ${token}`}},
      );

      // 404 = nenhum ponto cadastrado → segue para o formulário
      if (res.status === 404) return false;
      if (!res.ok) return false;

      const data = await res.json();

      // API pode retornar objeto vazio ou sem codigoPonto se não encontrar
      if (!data || (!data.codigoPonto && !data.nome)) return false;

      // Ponto encontrado — salva e redireciona conforme status
      setPontoExistente(data);
      if (data.status === 1) {
        // Ativo → manda pro painel myireit.com.br
        setStep('redirect');
      } else {
        // Pendente (0) / Suspenso (2) / Inativo (3) → mostra card de status
        setStep('existing');
      }
      return true;
    } catch {
      // Erro de rede ou 404 → não bloqueia, deixa seguir para o form
      return false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // VALIDATION
  // ─────────────────────────────────────────────────────────────────────────
  const validateInfo = () => {
    if (!nome.trim()) {
      setInfoError('Informe o nome do ponto.');
      return false;
    }
    if (!celular.trim()) {
      setInfoError('Informe o celular.');
      return false;
    }
    if (!email.trim()) {
      setInfoError('Informe o email do ponto.');
      return false;
    }
    if (temCnpj && !cnpj.trim()) {
      setInfoError('Informe o CNPJ.');
      return false;
    }
    setInfoError('');
    return true;
  };

  const validateAddress = () => {
    if (!cep || cepError) {
      setAddrError('CEP inválido.');
      return false;
    }
    if (!cidade.trim()) {
      setAddrError('Informe a cidade.');
      return false;
    }
    if (!logradouro.trim()) {
      setAddrError('Informe o logradouro.');
      return false;
    }
    if (!numero.trim()) {
      setAddrError('Informe o número.');
      return false;
    }
    setAddrError('');
    return true;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // LOGO UPLOAD (same pattern as Informacoes)
  // ─────────────────────────────────────────────────────────────────────────
  const {getRootProps, getInputProps} = useDropzone({
    maxFiles: 1,
    accept: {'image/*': []},
    onDrop: files =>
      setLogoFiles(
        files.map(f => Object.assign(f, {preview: URL.createObjectURL(f)})),
      ),
  });

  useEffect(
    () => () => logoFiles.forEach(f => URL.revokeObjectURL(f.preview)),
    [logoFiles],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────────────────────────────────────
  const generateCodigo = () => {
    const prefix = nome
      .trim()
      .slice(0, 3)
      .toUpperCase()
      .replace(/[^A-Z]/g, 'X');
    return `${prefix}${Date.now().toString().slice(-4)}`;
  };

  const handleSubmit = async () => {
    if (!authUser) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const token = await authUser.getIdToken();
      const uid = authUser.uid;
      const codigo = generateCodigo();
      const payload = {
        nome,
        nomeFantasia: nomeFantasia || nome,
        codigoPonto: codigo,
        imagem:
          logoFiles.length > 0
            ? `https://ireifiles.s3.us-east-1.amazonaws.com/${uid}/logo_ponto/${uid}`
            : '',
        temCnpj,
        cnpj: temCnpj ? cnpj : '',
        razaoSocial: temCnpj ? razaoSocial : '',
        celular,
        email,
        gestorNome: authUser.displayName || authName,
        gestorFirebaseid: uid,
        pais: 'Brasil',
        estado,
        cidade,
        bairro,
        logradouro,
        numero,
        cep,
        comp,
        lat: marker.lat,
        lng: marker.lng,
        tipoCobranca: 0,
        taxaPontoMotorista: 0,
        status: 0, // sempre pendente
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      const res = await fetch(`${API_BASE}/parceiros/pontos/insert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`status ${res.status}`);

      // Upload logo se houver
      if (logoFiles.length > 0 && logoFiles[0].path) {
        const form = new FormData();
        form.append('file', logoFiles[0]);
        await fetch(
          `${API_BASE}/s3/upload?docid=${uid}&userid=${uid}&svc=logo_ponto&index=0`,
          {
            method: 'POST',
            headers: {Authorization: `Bearer ${token}`},
            body: form,
          },
        ).catch(() => {});
      }

      setSubmitDone(true);
      setStep('done');
    } catch (err) {
      setSubmitError(
        'Erro ao enviar. Verifique sua conexão e tente novamente.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER STEPS
  // ──────────────────────────────────────────────────────────────────────────

  // ── STATUS CARDS (shown when user already has a ponto) ──────────────────────

  const STATUS_LABELS = {
    0: {
      label: 'Pendente',
      color: '#c2410c',
      bg: '#fff7ed',
      icon: '⏳',
      msg: 'Sua solicitação está em análise. Nossa equipe ativa pontos em até 24h.',
    },
    1: {
      label: 'Ativo',
      color: '#059669',
      bg: '#ecfdf5',
      icon: '✅',
      msg: 'Seu ponto está ativo! Acesse o painel para gerenciar sua frota.',
    },
    2: {
      label: 'Suspenso',
      color: '#d97706',
      bg: '#fef3c7',
      icon: '⚠️',
      msg: 'Seu ponto está suspenso. Entre em contato com o suporte.',
    },
    3: {
      label: 'Inativo',
      color: '#6b7280',
      bg: '#f3f4f6',
      icon: '❌',
      msg: 'Seu ponto está inativo. Entre em contato com o suporte.',
    },
  };

  // Ponto pendente / suspenso / inativo
  const renderExisting = () => {
    const p = pontoExistente;
    if (!p) return null;
    const stt = STATUS_LABELS[p.status] || STATUS_LABELS[0];
    return (
      <SlideUp delay="0.05s">
        <Card sm={sm}>
          <div style={{textAlign: 'center', marginBottom: 24}}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: stt.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32,
                margin: '0 auto 14px',
                border: `2px solid ${stt.color}30`,
              }}
            >
              {stt.icon}
            </div>
            <p
              style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}
            >
              Você já tem um ponto cadastrado
            </p>
            <p style={{margin: '6px 0 0', fontSize: 14, color: C.muted}}>
              Cada conta pode ter apenas um ponto vinculado.
            </p>
          </div>

          {/* Ponto info card */}
          <div
            style={{
              background: '#f8f9fb',
              borderRadius: 12,
              padding: '16px 18px',
              border: '1px solid #eef0f4',
              marginBottom: 20,
            }}
          >
            {/* Logo + nome */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 14,
              }}
            >
              {p.imagem ? (
                <img
                  src={p.imagem}
                  alt="logo"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 10,
                    objectFit: 'cover',
                    border: '1px solid #e0e5ef',
                  }}
                  onError={e => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 10,
                    background: '#e0e8f8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                  }}
                >
                  🏍️
                </div>
              )}
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: 700,
                    color: C.text,
                  }}
                >
                  {p.nome}
                </p>
                <p style={{margin: '2px 0 0', fontSize: 12, color: C.muted}}>
                  {p.codigoPonto}
                </p>
              </div>
              {/* Status badge */}
              <span
                style={{
                  marginLeft: 'auto',
                  background: stt.bg,
                  color: stt.color,
                  fontWeight: 700,
                  fontSize: 11,
                  padding: '4px 12px',
                  borderRadius: 20,
                }}
              >
                {stt.label}
              </span>
            </div>

            {/* Details */}
            {[
              {label: 'Cidade', value: `${p.cidade}, ${p.estado}`},
              {label: 'Celular', value: p.celular},
              {
                label: 'Motoristas/Mototaxistas',
                value: p.qtdMotoristasVinculados || 0,
              },
            ].map(r => (
              <div
                key={r.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '6px 0',
                  borderBottom: '1px solid #eef0f4',
                }}
              >
                <span style={{fontSize: 12, color: C.muted}}>{r.label}</span>
                <span style={{fontSize: 12, color: C.text, fontWeight: 600}}>
                  {r.value}
                </span>
              </div>
            ))}
          </div>

          {/* Status message */}
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 10,
              background: stt.bg,
              border: `1px solid ${stt.color}30`,
              marginBottom: 24,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 13,
                color: stt.color,
                fontWeight: 600,
              }}
            >
              {stt.msg}
            </p>
          </div>

          {/* CTA: support or logout */}
          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            <Button
              href="mailto:suporte@ireit.com.br"
              sx={{
                bgcolor: C.azul,
                color: '#fff',
                borderRadius: '12px',
                height: 46,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 14,
                ':hover': {bgcolor: C.azulD},
              }}
            >
              Falar com suporte
            </Button>
            <Button
              component={Link}
              to="/"
              sx={{
                color: C.muted,
                borderRadius: '12px',
                height: 46,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                ':hover': {color: C.text},
              }}
            >
              Voltar ao início
            </Button>
          </div>
        </Card>
      </SlideUp>
    );
  };

  // Ponto ativo → redirect card
  const renderRedirect = () => {
    const p = pontoExistente;
    return (
      <SlideUp delay="0.05s">
        <Card sm={sm}>
          <div style={{textAlign: 'center', padding: '8px 0 24px'}}>
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 32,
                margin: '0 auto 16px',
                border: '2px solid #05966930',
              }}
            >
              ✅
            </div>
            <p
              style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}
            >
              Seu ponto está ativo!
            </p>
            <p
              style={{
                margin: '8px auto 0',
                fontSize: 14,
                color: C.muted,
                maxWidth: 340,
                lineHeight: 1.6,
              }}
            >
              O gerenciamento do ponto é feito pelo painel exclusivo para
              gestores. Clique abaixo para acessar.
            </p>

            {p && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  background: '#f8f9fb',
                  borderRadius: 12,
                  padding: '12px 16px',
                  margin: '20px 0',
                  border: '1px solid #eef0f4',
                  textAlign: 'left',
                }}
              >
                {p.imagem ? (
                  <img
                    src={p.imagem}
                    alt="logo"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      objectFit: 'cover',
                    }}
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      background: '#e0e8f8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 20,
                    }}
                  >
                    🏍️
                  </div>
                )}
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      fontWeight: 700,
                      color: C.text,
                    }}
                  >
                    {p.nome}
                  </p>
                  <p style={{margin: '2px 0 0', fontSize: 12, color: C.muted}}>
                    {p.codigoPonto} · {p.cidade}, {p.estado}
                  </p>
                </div>
                <span
                  style={{
                    marginLeft: 'auto',
                    background: '#ecfdf5',
                    color: '#059669',
                    fontWeight: 700,
                    fontSize: 11,
                    padding: '4px 12px',
                    borderRadius: 20,
                  }}
                >
                  Ativo
                </span>
              </div>
            )}

            <Button
              href="https://myireit.com.br"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: C.laranja,
                color: '#fff',
                borderRadius: '12px',
                height: 52,
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                width: '100%',
                mb: 1,
                ':hover': {bgcolor: C.laranjaD},
              }}
            >
              Acessar painel do gestor →
            </Button>
            <p style={{margin: '10px 0 0', fontSize: 12, color: C.muted}}>
              myireit.com.br · Use a mesma conta IREIT para entrar
            </p>
          </div>
        </Card>
      </SlideUp>
    );
  };

  // ── STEP 1: Auth ──────────────────────────────────────────────────────────
  const renderAuth = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        {/* Toggle */}
        <div
          style={{
            display: 'flex',
            borderRadius: 12,
            background: '#f4f6fa',
            padding: 4,
            marginBottom: 24,
          }}
        >
          {['register', 'login'].map(m => (
            <button
              key={m}
              onClick={() => {
                setAuthMode(m);
                setAuthError('');
              }}
              style={{
                flex: 1,
                padding: '9px 0',
                border: 'none',
                borderRadius: 9,
                background: authMode === m ? '#fff' : 'transparent',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
                color: authMode === m ? C.azul : C.muted,
                boxShadow:
                  authMode === m ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
              }}
            >
              {m === 'register' ? 'Criar conta' : 'Já tenho conta'}
            </button>
          ))}
        </div>

        <STitle sub="Usaremos sua conta para identificar o ponto e o gestor.">
          {authMode === 'register'
            ? 'Crie sua conta de gestor'
            : 'Entre na sua conta'}
        </STitle>

        {authMode === 'register' && (
          <TextField
            label="Seu nome completo"
            variant="outlined"
            size="small"
            fullWidth
            style={{marginBottom: 14}}
            value={authName}
            disabled={authLoading}
            onChange={e => setAuthName(e.target.value)}
            autoComplete="name"
          />
        )}

        <TextField
          label="Email"
          type="email"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={authEmail}
          disabled={authLoading}
          onChange={e => setAuthEmail(e.target.value)}
          autoComplete="email"
        />

        <TextField
          label="Senha"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 8}}
          type={showPass ? 'text' : 'password'}
          value={authPass}
          disabled={authLoading}
          onChange={e => setAuthPass(e.target.value)}
          autoComplete={
            authMode === 'register' ? 'new-password' : 'current-password'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPass(v => !v)}
                  edge="end"
                >
                  {showPass ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') handleEmailAuth();
          }}
        />

        {authError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              textAlign: 'center',
              marginBottom: 8,
              marginTop: 4,
            }}
          >
            {authError}
          </p>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleEmailAuth}
          disabled={authLoading}
          sx={{
            bgcolor: C.azul,
            ':hover': {bgcolor: C.azulD},
            borderRadius: '12px',
            height: 50,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            mt: '8px',
          }}
        >
          {authLoading ? (
            <CircularProgress size={22} style={{color: '#fff'}} />
          ) : authMode === 'register' ? (
            'Criar conta e continuar'
          ) : (
            'Entrar e continuar'
          )}
        </Button>

        <Divider style={{margin: '18px 0'}}>
          <span style={{fontSize: 12, color: C.muted, padding: '0 8px'}}>
            ou continuar com
          </span>
        </Divider>

        {/* Google */}
        <button
          onClick={handleGoogle}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: '1px solid #e0e0e0',
            background: '#fff',
            color: '#3c4043',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "'Outfit','Segoe UI',sans-serif",
            marginBottom: 10,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
          onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continuar com Google
        </button>

        {/* Apple */}
        <button
          onClick={handleApple}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: 'none',
            background: '#000',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
            fontFamily: "'Outfit','Segoe UI',sans-serif",
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#222')}
          onMouseLeave={e => (e.currentTarget.style.background = '#000')}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          Continuar com Apple
        </button>
      </Card>
    </SlideUp>
  );

  // ── STEP 2: Info do Ponto ─────────────────────────────────────────────────
  const renderInfo = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Informações principais do seu ponto de mototáxi.">
          Dados do Ponto
        </STitle>

        <TextField
          label="Nome do ponto *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={nome}
          onChange={e => setNome(e.target.value)}
          placeholder="Ex: Ponto Central da Pituba"
        />

        <TextField
          label="Nome fantasia (opcional)"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={nomeFantasia}
          onChange={e => setNomeFantasia(e.target.value)}
          placeholder="Como seu ponto é conhecido no bairro"
        />

        <TextField
          label="Celular / WhatsApp *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={celular}
          onChange={e => setCelular(e.target.value)}
          placeholder="(71) 99999-9999"
        />

        <TextField
          label="Email do ponto *"
          type="email"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 16}}
          value={email}
          onChange={e => setEmail(e.target.value)}
        />

        <Divider style={{margin: '4px 0 16px'}} />

        <FormControlLabel
          control={
            <Switch
              checked={temCnpj}
              onChange={e => setTemCnpj(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {color: C.laranja},
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  bgcolor: C.laranja,
                },
              }}
            />
          }
          label={
            <span style={{fontSize: 14, color: C.text, fontWeight: 600}}>
              Possuo CNPJ
            </span>
          }
        />

        {temCnpj && (
          <>
            <TextField
              label="CNPJ"
              variant="outlined"
              size="small"
              fullWidth
              style={{marginTop: 14}}
              value={cnpj}
              onChange={e => setCnpj(e.target.value)}
              placeholder="00.000.000/0001-00"
            />
            <TextField
              label="Razão social"
              variant="outlined"
              size="small"
              fullWidth
              style={{marginTop: 14}}
              value={razaoSocial}
              onChange={e => setRazaoSocial(e.target.value)}
            />
          </>
        )}

        {infoError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            {infoError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('auth')}
          onNext={() => {
            if (validateInfo()) setStep('logo');
          }}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP 3: Logo ──────────────────────────────────────────────────────────
  const renderLogo = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="A logo aparece para os motoristas/mototaxistas no app ao buscar um ponto.">
          Logo do Ponto
        </STitle>

        {/* Dropzone — mesmo padrão do Informacoes */}
        <div
          style={{display: 'flex', justifyContent: 'center', marginBottom: 24}}
        >
          <div style={{position: 'relative'}}>
            {logoFiles.length > 0 && (
              <IconButton
                onClick={() => setLogoFiles([])}
                size="small"
                style={{
                  position: 'absolute',
                  top: -12,
                  right: -12,
                  zIndex: 2,
                  background: '#fff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                }}
              >
                <CancelIcon style={{fontSize: 18, color: '#e53935'}} />
              </IconButton>
            )}
            <div
              {...getRootProps()}
              style={{
                width: 160,
                height: 160,
                borderRadius: 16,
                border: logoFiles.length > 0 ? 'none' : `2px dashed ${C.azul}`,
                background: logoFiles.length > 0 ? 'transparent' : '#f4f6fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'border 0.2s, background 0.2s',
              }}
            >
              <input {...getInputProps()} />
              {logoFiles.length > 0 ? (
                <img
                  src={logoFiles[0].preview}
                  alt="logo"
                  style={{
                    width: 160,
                    height: 160,
                    objectFit: 'cover',
                    borderRadius: 16,
                  }}
                />
              ) : (
                <div style={{textAlign: 'center', padding: 16}}>
                  <ImageIcon
                    style={{fontSize: 48, color: C.azul, opacity: 0.4}}
                  />
                  <p
                    style={{
                      margin: '8px 0 0',
                      fontSize: 13,
                      color: C.muted,
                      lineHeight: 1.4,
                    }}
                  >
                    Arraste ou clique
                    <br />
                    para adicionar logo
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <p
          style={{
            textAlign: 'center',
            fontSize: 13,
            color: C.muted,
            background: '#f4f6fa',
            borderRadius: 10,
            padding: '10px 16px',
          }}
        >
          PNG ou JPG. Tamanho recomendado: 400×400px. Você poderá alterar depois
          no painel.
        </p>

        <NavRow
          onBack={() => setStep('info')}
          onNext={() => setStep('address')}
          nextLabel={logoFiles.length > 0 ? 'Continuar' : 'Pular por enquanto'}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP 4: Address ───────────────────────────────────────────────────────
  const renderAddress = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Endereço físico do seu ponto para os motoristas/mototaxistas encontrarem.">
          Localização do Ponto
        </STitle>

        {/* CEP */}
        <InputMask
          mask="99999-999"
          value={cep}
          onChange={e => {
            setCep(e.target.value);
            const clean = e.target.value.replace(/\D/g, '');
            if (clean.length === 8) fetchCep(clean);
            else if (clean.length > 0) setCepError(true);
            else setCepError(false);
          }}
        >
          {() => (
            <TextField
              label="CEP *"
              variant="outlined"
              size="small"
              fullWidth
              style={{marginBottom: 6}}
              error={cepError}
              helperText={cepError ? 'CEP não encontrado.' : ''}
            />
          )}
        </InputMask>

        <Button
          size="small"
          style={{
            textTransform: 'none',
            color: C.laranja,
            fontSize: 12,
            marginBottom: 14,
          }}
          target="_blank"
          href="https://buscacepinter.correios.com.br/app/endereco/index.php"
        >
          Não sabe o CEP? Busque aqui
        </Button>

        <div style={{display: 'flex', gap: 12, marginBottom: 14}}>
          <TextField
            label="Estado"
            variant="outlined"
            size="small"
            style={{flex: '0 0 80px'}}
            value={estado}
            onChange={e => setEstado(e.target.value)}
            disabled
          />
          <TextField
            label="Cidade *"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={cidade}
            onChange={e => setCidade(e.target.value)}
            disabled
          />
        </div>

        <TextField
          label="Bairro"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={bairro}
          onChange={e => setBairro(e.target.value)}
        />

        <TextField
          label="Logradouro *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={logradouro}
          onChange={e => setLogradouro(e.target.value)}
          placeholder="Rua, Avenida, Praça..."
        />

        <div style={{display: 'flex', gap: 12, marginBottom: 14}}>
          <TextField
            label="Número *"
            variant="outlined"
            size="small"
            style={{flex: '0 0 100px'}}
            value={numero}
            onChange={e => setNumero(e.target.value)}
            type="number"
          />
          <TextField
            label="Complemento"
            variant="outlined"
            size="small"
            style={{flex: 1}}
            value={comp}
            onChange={e => setComp(e.target.value)}
            placeholder="Sala, Loja..."
          />
        </div>

        {/* Map — click anywhere or drag the pin to set the exact position */}
        {mapsLoaded && mapReady && (
          <div
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              marginBottom: 14,
              border: '1px solid #e0e5ef',
            }}
          >
            {/* Header hint */}
            <div
              style={{
                background: '#f4f6fa',
                padding: '8px 14px',
                fontSize: 12,
                color: C.muted,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <MyLocation style={{fontSize: 14, color: C.laranja}} />
              <span>
                Clique no mapa ou{' '}
                <strong style={{color: C.text}}>
                  arraste o marcador laranja
                </strong>{' '}
                para ajustar a posição exata
              </span>
            </div>

            <GoogleMap
              mapContainerStyle={{width: '100%', height: 260}}
              center={marker}
              zoom={17}
              options={{
                disableDefaultUI: false,
                zoomControl: true,
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                clickableIcons: false,
              }}
              onClick={e => {
                if (e.latLng)
                  setMarker({lat: e.latLng.lat(), lng: e.latLng.lng()});
              }}
            >
              <Marker
                position={marker}
                draggable={true}
                onDragEnd={e => {
                  if (e.latLng)
                    setMarker({lat: e.latLng.lat(), lng: e.latLng.lng()});
                }}
                icon={{
                  // Custom orange teardrop pin matching IREIT brand
                  path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
                  fillColor: '#FF6600',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2.5,
                  scale: 2.2,
                  anchor: {x: 12, y: 24},
                }}
              />
            </GoogleMap>

            {/* Coordinate confirmation strip */}
            <div
              style={{
                background: '#fff',
                borderTop: '1px solid #e0e5ef',
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#FF6600',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <span style={{fontSize: 12, color: C.text, fontWeight: 700}}>
                Ponto marcado:
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: C.muted,
                  fontFamily: 'monospace',
                  letterSpacing: '0.03em',
                }}
              >
                {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
              </span>
            </div>
          </div>
        )}

        {/* Fallback button if geocode hasn't fired (address without number) */}
        {mapsLoaded && !mapReady && logradouro && cidade && (
          <button
            onClick={geocode}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(6,68,142,0.06)',
              border: '1px solid #d0d8ee',
              borderRadius: 8,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 13,
              color: C.azul,
              fontWeight: 600,
              marginBottom: 14,
              width: '100%',
              justifyContent: 'center',
              fontFamily: "'Outfit','Segoe UI',sans-serif",
            }}
          >
            <MyLocation style={{fontSize: 16}} />
            Localizar no mapa
          </button>
        )}

        {addrError && (
          <p style={{color: '#e53935', fontSize: 13, marginBottom: 0}}>
            {addrError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('logo')}
          onNext={() => {
            if (validateAddress()) setStep('confirm');
          }}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP 5: Confirm ───────────────────────────────────────────────────────
  const renderConfirm = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Revise e envie seu cadastro.">Resumo do Cadastro</STitle>

        {/* Summary rows */}
        {[
          {label: 'Ponto', value: nome},
          {label: 'Gestor', value: authUser?.displayName || authName},
          {label: 'Celular', value: celular},
          {label: 'Email', value: email},
          {label: 'Cidade', value: `${cidade}, ${estado}`},
          {
            label: 'Endereço',
            value: `${logradouro}, ${numero}${bairro ? ` — ${bairro}` : ''}`,
          },
          {label: 'CNPJ', value: temCnpj ? cnpj : 'Não informado'},
          {
            label: 'Logo',
            value:
              logoFiles.length > 0
                ? '✅ Adicionada'
                : '⚠️ Sem logo (pode adicionar depois)',
          },
        ].map(row => (
          <div
            key={row.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid #f0f2f5',
            }}
          >
            <span style={{fontSize: 13, color: C.muted, fontWeight: 500}}>
              {row.label}
            </span>
            <span
              style={{
                fontSize: 13,
                color: C.text,
                fontWeight: 600,
                textAlign: 'right',
                maxWidth: '60%',
              }}
            >
              {row.value}
            </span>
          </div>
        ))}

        {/* Status notice */}
        <div
          style={{
            marginTop: 20,
            padding: '12px 16px',
            borderRadius: 10,
            background: 'rgba(255,102,0,0.07)',
            border: `1px solid rgba(255,102,0,0.2)`,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: C.laranjaD,
              fontWeight: 600,
            }}
          >
            ⏳ Cadastro ficará como <strong>Pendente</strong>
          </p>
          <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
            Nossa equipe revisará e ativará em até 24h. Você receberá um email
            de confirmação.
          </p>
        </div>

        {submitError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            {submitError}
          </p>
        )}

        <NavRow
          onBack={() => setStep('address')}
          onNext={handleSubmit}
          nextLabel="Enviar cadastro"
          loading={submitting}
        />
      </Card>
    </SlideUp>
  );

  // ── STEP DONE ─────────────────────────────────────────────────────────────
  const renderDone = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <div style={{textAlign: 'center', padding: '8px 0 16px'}}>
          <CheckCircleOutline
            style={{fontSize: 72, color: C.success, marginBottom: 12}}
          />
          <p style={{margin: 0, fontSize: 22, fontWeight: 800, color: C.text}}>
            Ponto cadastrado!
          </p>
          <p
            style={{
              margin: '8px 0 24px',
              fontSize: 14,
              color: C.muted,
              lineHeight: 1.6,
            }}
          >
            Recebemos seu cadastro. Nossa equipe irá revisar e ativar seu ponto
            em até 24h. Você receberá um email quando estiver tudo certo.
          </p>

          <div
            style={{
              background: '#f4f6fa',
              borderRadius: 12,
              padding: '14px 20px',
              marginBottom: 24,
              textAlign: 'left',
            }}
          >
            <p
              style={{
                margin: '0 0 8px',
                fontSize: 12,
                fontWeight: 700,
                color: C.muted,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Próximos passos
            </p>
            {[
              'Aguarde a ativação por email (até 24h)',
              'Acesse o painel do gestor com sua conta',
              'Comece a aceitar motoristas/mototaxistas no seu ponto',
            ].map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  marginBottom: 6,
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: C.azul,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </span>
                <span style={{fontSize: 13, color: C.text}}>{t}</span>
              </div>
            ))}
          </div>

          <Button
            component={Link}
            to="/"
            sx={{
              bgcolor: C.laranja,
              color: '#fff',
              borderRadius: '12px',
              height: 48,
              px: 4,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 15,
              ':hover': {bgcolor: C.laranjaD},
            }}
          >
            Voltar ao início
          </Button>
        </div>
      </Card>
    </SlideUp>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // PAGE
  // ──────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        overflowX: 'hidden',
        width: '100%',
        fontFamily: "'Outfit','Segoe UI',sans-serif",
      }}
    >
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 2}}
          size="small"
          color="primary"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      {/* Navbar */}
      <div
        style={{
          background: C.azul,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          zIndex: 9999,
          top: 0,
          left: 0,
          width: '100%',
        }}
      >
        <Button
          component={Link}
          to="/"
          style={{
            width: 95,
            height: 47,
            marginLeft: 12,
            backgroundImage: `url(${logo})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        />
        {sm || vsm ? (
          <MenuIcon
            style={{color: '#fff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        ) : (
          <Tab />
        )}
      </div>
      {(sm || vsm) && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      {/* HERO */}
      <div
        style={{
          background: `linear-gradient(145deg, ${C.azul} 0%, ${C.azul} 35%, ${C.roxo} 75%, ${C.roxo} 100%)`,
          paddingTop: sm ? 80 : 100,
          paddingBottom: sm ? 40 : 60,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 350,
            height: 350,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(255,102,0,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 260,
            height: 260,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(124,83,227,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            maxWidth: 620,
            margin: '0 auto',
            padding: sm ? '0 16px' : '0 24px',
            textAlign: 'center',
          }}
        >
          <SlideUp delay="0.05s">
            {/* Badge */}
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 20,
                background: 'rgba(255,102,0,0.18)',
                border: '1px solid rgba(255,102,0,0.4)',
                borderRadius: 20,
                padding: '5px 16px',
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: C.laranja,
                  display: 'inline-block',
                }}
              />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.laranja,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Novo Recurso
              </span>
            </div>
          </SlideUp>

          <SlideUp delay="0.1s">
            <h1
              style={{
                color: '#fff',
                fontSize: sm ? 30 : 44,
                fontWeight: 800,
                margin: '0 0 12px',
                lineHeight: 1.1,
                letterSpacing: -0.5,
              }}
            >
              Cadastre seu <span style={{color: C.laranja}}>Ponto</span>
              <br />
              no app IREIT
            </h1>
          </SlideUp>

          <SlideUp delay="0.15s">
            <p
              style={{
                color: 'rgba(255,255,255,0.65)',
                fontSize: sm ? 15 : 18,
                margin: '0 auto 28px',
                maxWidth: 480,
                lineHeight: 1.55,
              }}
            >
              Gerencie sua frota de mototáxi, táxi ou carros de aplicativo —
              tudo num só lugar. Sem mensalidade.
            </p>
          </SlideUp>

          {/* Benefits */}
          <SlideUp delay="0.2s">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                justifyContent: 'center',
                marginBottom: step !== 'done' ? 0 : 32,
              }}
            >
              {benefits.map(b => (
                <span
                  key={b.text}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: 20,
                    padding: '5px 12px',
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.85)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  {b.icon} {b.text}
                </span>
              ))}
            </div>
          </SlideUp>

          {/* Pricing tag */}
          <SlideUp delay="0.25s">
            <div
              style={{
                display: 'inline-flex',
                gap: 12,
                marginTop: 20,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              {[
                {label: 'Taxa por corrida', value: 'R$ 0,49–0,79'},
                {label: 'ou percentual', value: '10% do valor'},
                {label: 'Mensalidade', value: 'Grátis'},
              ].map(item => (
                <div
                  key={item.label}
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    borderRadius: 12,
                    padding: '10px 18px',
                    border: '1px solid rgba(255,255,255,0.12)',
                    textAlign: 'center',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: 11,
                      color: 'rgba(255,255,255,0.5)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {item.label}
                  </p>
                  <p
                    style={{
                      margin: '3px 0 0',
                      fontSize: 16,
                      fontWeight: 800,
                      color: '#fff',
                    }}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </SlideUp>
        </div>
      </div>

      {/* FORM AREA */}
      <div
        style={{
          background: C.bg,
          padding: sm ? '32px 12px 60px' : '48px 24px 80px',
        }}
      >
        {step !== 'done' && step !== 'existing' && step !== 'redirect' && (
          <div style={{maxWidth: 620, margin: '0 auto'}}>
            <StepBar current={step} sm={sm} />
          </div>
        )}

        <div style={{maxWidth: 520, margin: '0 auto'}}>
          {step === 'auth' && renderAuth()}
          {step === 'existing' && renderExisting()}
          {step === 'redirect' && renderRedirect()}
          {step === 'info' && renderInfo()}
          {step === 'logo' && renderLogo()}
          {step === 'address' && renderAddress()}
          {step === 'confirm' && renderConfirm()}
          {step === 'done' && renderDone()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PontoCadastro;
